import {
  Component,
  ElementRef, EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren
} from '@angular/core';

import {TranslocoService} from "@ngneat/transloco";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {AbstractComponent, ConfirmationModalComponent} from "@components";
import {
  EventItem,
  EventPaymentSettings, Lookups,
  OrganiserPaymentData,
  Race,
  RaceAdditionalPricingItem, RaceCommercialItem, VisibilityTypes, TOAST_STATE
} from "@model";
import {RaceCommercialService, RaceCommercialServiceErrors, ToastService} from "@services";
import {ActivatedRoute} from '@angular/router';
import {RaceCommercialItemFormComponent} from "../race-commercial-item-form/race-commercial-item-form.component";
import {
  RaceAdditionalPricingItemFormComponent
} from "../race-additional-pricing-item-form/race-additional-pricing-item-form.component";

@Component({
    selector: 'app-race-additional-pricing-item-card',
    templateUrl: './race-additional-pricing-item-card.component.html',
    styleUrls: ['./race-additional-pricing-item-card.component.scss'],
    standalone: false
})
export class RaceAdditionalPricingItemCardComponent extends AbstractComponent implements OnInit {
  @Input() event: EventItem | undefined;
  @Input() race: Race | undefined;
  @Input() disableEditing: boolean = false;
  @Input() eventPaymentSettings: EventPaymentSettings | null = null;
  @Input() organiserPaymentData: OrganiserPaymentData | null = null;
  @Input() raceAdditionalItems: RaceAdditionalPricingItem[] = [];
  @Input() currencies: Lookups[] = [];
  @Input() visibilityTypes: Lookups[] = [];
  @Input() displayFlags: Lookups[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @Output() raceAdditionalItemsChanged = new EventEmitter<RaceAdditionalPricingItem[]>();
  @ViewChildren('pricingItemValue') pricingItemValuesQuery?: QueryList<ElementRef>;

  currentRaceAdditionalItem: RaceAdditionalPricingItem = new RaceAdditionalPricingItem();
  additionalPricingItemsCollapsed: boolean = false;

  constructor(public translocoService: TranslocoService, private raceCommercialService: RaceCommercialService, private route: ActivatedRoute,
              private toastService: ToastService, private modalService: NgbModal) {
    super();
  }

  ngOnInit(): void {
    this.currentRaceAdditionalItem.priceConstant = true;
  }

  getCurrencyLabel(): string {
    const curr = this.currencies.find((currency) => currency.id === this.event?.currency);
    return curr?.name ?? "";
  }

  onRaceAdditionalItemsChanged(raceAdditionalItems: RaceAdditionalPricingItem[]): void {
    this.raceAdditionalItemsChanged.emit(raceAdditionalItems);
  }

  openEditModal(item?: RaceAdditionalPricingItem) {
    const modalRef = this.modalService.open(RaceAdditionalPricingItemFormComponent, {
      centered: true,
      size: 'xl',
      scrollable: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate(item ? "race.pricing.add.additional.item" : "race.pricing.edit.additional.item");
    modalRef.componentInstance.currentRaceAdditionalItem = item ?? new RaceAdditionalPricingItem();
    modalRef.componentInstance.event = this.event;
    modalRef.componentInstance.race = this.race;
    modalRef.componentInstance.currencies = this.currencies;
    modalRef.componentInstance.visibilityTypes = [...this.visibilityTypes.filter(item => item.id == VisibilityTypes.NOT_VISIBLE || item.id == VisibilityTypes.VISIBLE_TO_ADMINISTRATION)];
    modalRef.componentInstance.raceAdditionalItems = this.raceAdditionalItems;
    modalRef.componentInstance.disableEditing = this.disableEditing;
    modalRef.componentInstance.eventPaymentSettings = this.eventPaymentSettings;
    modalRef.componentInstance.organiserPaymentData = this.organiserPaymentData;
    modalRef.componentInstance.registrationDisplayFlags = this.displayFlags;

    modalRef.result.then((raceAdditionalPricingItem: RaceAdditionalPricingItem) => {
      this.raceCommercialService.updateRaceAdditionalPricingItem(raceAdditionalPricingItem, () => {
        this.toastService.showToast(TOAST_STATE.success,
          this.translocoService.translate("race.pricing.additional.item.save.success"));
        this.raceCommercialService.findRaceAdditionalPricingItems(this.race!.id!).subscribe((raceAdditionalItems: RaceAdditionalPricingItem[]) => {
          this.raceAdditionalItems = raceAdditionalItems;
          this.raceAdditionalItemsChanged.emit(this.raceAdditionalItems);
        });
      }, () => {
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("race.pricing.additional.item.save.error"))
      });
    }, (_) => {
      /// ignore
    });
  }

  onDeleteRaceAdditionalPricingItem(raceAdditionalPricingItem: RaceAdditionalPricingItem) {
    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });

    modalRef.componentInstance.title = this.translocoService.translate("race.pricing.additional.item.delete.dialog.title");
    modalRef.componentInstance.text = this.translocoService.translate("race.pricing.additional.item.delete.dialog.text");

    modalRef.result.then((_) => {
      this.deletePricingAdditionalItem(raceAdditionalPricingItem);
    }, (_) => {
      /// ignore
    });
  }

  deletePricingAdditionalItem(raceAdditionalItem: RaceAdditionalPricingItem) {
    raceAdditionalItem.raceId = this.race?.id;
    this.raceCommercialService.deleteRaceAdditionalPricingItem(raceAdditionalItem,
      () => {
        this.toastService.showToast(TOAST_STATE.success,
          this.translocoService.translate("race.pricing.additional.item.delete.success"));
        this.raceCommercialService.findRaceAdditionalPricingItems(this.race!.id!).subscribe((raceAdditionalItems: RaceAdditionalPricingItem[]) => {
          this.raceAdditionalItemsChanged.emit(raceAdditionalItems);
        });
      }, (errorCode: number) => {
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate(errorCode === RaceCommercialServiceErrors.ADDITIONAL_PRICING_ITEM_IN_USE ? "race.pricing.additional.item.delete.error.18" :"race.pricing.additional.item.delete.error"))
      });
  }
}
